import React, { createContext, useState, useCallback, useRef } from "react";
import { SnackbarConfig } from "../../../DataModels/DataModels";

interface SnackbarContextType {
	showSnackbar: (config: SnackbarConfig) => void;
	hideSnackbar: (id: string) => void;
}

export const SnackbarContext = createContext<SnackbarContextType | null>(null);

interface ActiveSnackbar extends SnackbarConfig {
	isVisible: boolean;
	isAnimating: boolean;
}

interface SnackbarProviderProps {
	children: React.ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
	children
}) => {
	const [snackbars, setSnackbars] = useState<ActiveSnackbar[]>([]);
	const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

	const removeSnackbar = useCallback((id: string) => {
		setSnackbars((prev) => prev.filter((snack) => snack.id !== id));

		// Clear timeout if it exists
		const timeout = timeoutRefs.current.get(id);
		if (timeout) {
			clearTimeout(timeout);
			timeoutRefs.current.delete(id);
		}
	}, []);

	const hideSnackbar = useCallback(
		(id: string) => {
			// Start exit animation
			setSnackbars((prev) =>
				prev.map((snack) =>
					snack.id === id
						? { ...snack, isVisible: false, isAnimating: true }
						: snack
				)
			);

			// Remove after animation
			setTimeout(() => {
				removeSnackbar(id);
			}, 300); // Animation duration
		},
		[removeSnackbar]
	);

	const showSnackbar = useCallback(
		(config: SnackbarConfig) => {
			const newSnackbar: ActiveSnackbar = {
				...config,
				isVisible: false,
				isAnimating: true
			};
			console.log(`showSnackbar.Provider: [${config.component.name}]`);

			setSnackbars((prev) => [...prev, newSnackbar]);

			// Start enter animation
			requestAnimationFrame(() => {
				setSnackbars((prev) =>
					prev.map((snack) =>
						snack.id === config.id ? { ...snack, isVisible: true } : snack
					)
				);
			});

			// Set timeout for auto-hide
			const duration = config.duration || 5000;
			const timeout = setTimeout(() => {
				hideSnackbar(config.id);
			}, duration);

			timeoutRefs.current.set(config.id, timeout);
		},
		[hideSnackbar]
	);

	const contextValue: SnackbarContextType = {
		showSnackbar,
		hideSnackbar
	};

	return (
		<SnackbarContext.Provider value={contextValue}>
			{children}

			{/* Snackbar Container */}
			<div className="fixed inset-0 pointer-events-none z-snackbar">
				<div className="flex flex-col items-center justify-end h-full pb-8 space-y-2">
					{snackbars.map((snackbar) => {
						const SnackbarComponent = snackbar.component;

						return (
							<div
								key={snackbar.id}
								className={`max-w-[370px] w-[370px] pointer-events-auto transform transition-all duration-300 ease-in-out ${
									snackbar.isVisible
										? "translate-y-0 opacity-100 scale-100"
										: "translate-y-full opacity-0 scale-95"
								}`}
							>
								<SnackbarComponent
									theme={snackbar.theme}
									onGoAway={() => hideSnackbar(snackbar.id)}
								/>
							</div>
						);
					})}
				</div>
			</div>
		</SnackbarContext.Provider>
	);
};
